import { AppError } from '@/core/errors/AppError.js';
import type { BulkPromotionStatusInput, ClonePromotionInput } from '@/features/promotion/domain/repositories/IPromotionRepository.js';
import { getSupabaseClient } from '@/core/supabase/client.js';
import { fetchAllPaged } from '@/core/supabase/pagination.js';
import type { PromotionScheme } from '@/features/promotion/domain/entities/PromotionScheme.js';

const SCHEME_FIELDS = `id, project_id, code, name, mechanism, start_date, end_date, conditions, settings, rewards, enable, created_at, updated_at`;

export class PromotionRemoteDataSource {
  private get supabase() { return getSupabaseClient() as any; }

  private mapToEntity(item: any): PromotionScheme {
    if (!item) return item;
    if (item.project_id) { 
      item.projectId = item.project_id; 
      delete item.project_id; 
    }
    
    // Extract advanced fields from the JSONB 'settings' column
    if (item.settings) {
      if (item.settings.crossPromotionMode !== undefined) item.crossPromotionMode = item.settings.crossPromotionMode;
      if (item.settings.maxSplittedBills !== undefined) item.maxSplittedBills = item.settings.maxSplittedBills;
      if (item.settings.splitBillsByPrice !== undefined) item.splitBillsByPrice = item.settings.splitBillsByPrice;
      if (item.settings.luckyWheelSettings !== undefined) item.luckyWheelSettings = item.settings.luckyWheelSettings;
      if (item.settings.selectiveSettings !== undefined) item.selectiveSettings = item.settings.selectiveSettings;
    }
    
    return item as PromotionScheme;
  }

  private mapToDB(input: any): any {
    const payload = { ...input };
    if ('projectId' in payload) { 
      payload.project_id = payload.projectId; 
      delete payload.projectId; 
    }
    
    // Pack advanced fields into the 'settings' JSONB column
    const settings: any = payload.settings || {};
    let hasSettingsUpdate = false;
    
    if ('crossPromotionMode' in payload) { settings.crossPromotionMode = payload.crossPromotionMode; delete payload.crossPromotionMode; hasSettingsUpdate = true; }
    if ('maxSplittedBills' in payload) { settings.maxSplittedBills = payload.maxSplittedBills; delete payload.maxSplittedBills; hasSettingsUpdate = true; }
    if ('splitBillsByPrice' in payload) { settings.splitBillsByPrice = payload.splitBillsByPrice; delete payload.splitBillsByPrice; hasSettingsUpdate = true; }
    if ('luckyWheelSettings' in payload) { settings.luckyWheelSettings = payload.luckyWheelSettings; delete payload.luckyWheelSettings; hasSettingsUpdate = true; }
    if ('selectiveSettings' in payload) { settings.selectiveSettings = payload.selectiveSettings; delete payload.selectiveSettings; hasSettingsUpdate = true; }
    
    if (hasSettingsUpdate || payload.settings) {
      payload.settings = settings;
    }
    
    return payload;
  }

  private operationError(error: { code?: string }): AppError {
    const messages: Record<string, string> = {
      '42501': 'Bạn không có quyền quản lý khuyến mãi trong dự án này.',
      '22023': 'Dữ liệu không hợp lệ. Vui lòng kiểm tra và thử lại.',
      'P0002': 'Khuyến mãi không còn khả dụng. Vui lòng làm mới danh sách.',
      '23505': 'Mã khuyến mãi đã tồn tại. Vui lòng chọn mã khác.',
    };
    return new AppError(messages[error.code ?? ''] ?? 'Không thể lưu thay đổi. Vui lòng kiểm tra kết nối và thử lại.', error.code);
  }

  async existsByCode(projectId: string, code: string, excludeId?: string): Promise<boolean> {
    const trimmedCode = code.trim();
    if (!trimmedCode) return false;
    let query = this.supabase
      .from('promotion_schemes')
      .select('id', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .ilike('code', trimmedCode);
    if (excludeId) query = query.neq('id', excludeId);
    const { count, error } = await query;
    if (error) throw error;
    return (count ?? 0) > 0;
  }

  async bulkSetEnabled(input: BulkPromotionStatusInput): Promise<string[]> {
    const { data, error } = await this.supabase.rpc('promotion_schemes_bulk_set_enabled', {
      p_project_id: input.projectId, p_ids: input.ids, p_enabled: input.enabled,
    });
    if (error) throw this.operationError(error);
    return data as string[];
  }

  async cloneScheme(input: ClonePromotionInput): Promise<PromotionScheme> {
    const { data, error } = await this.supabase.rpc('promotion_scheme_clone', {
      p_project_id: input.projectId, p_source_id: input.sourceId,
      p_name: input.name.trim(), p_code: input.code.trim(),
    }).single();
    if (error) throw this.operationError(error);
    return this.mapToEntity(data);
  }

  async listSchemes(options: { orgId: string; projectId: string; search?: string; enabled?: boolean }): Promise<PromotionScheme[]> {
    const data = await fetchAllPaged<any>((from, to) => {
      let q = this.supabase.from('promotion_schemes')
        .select(SCHEME_FIELDS)
        .eq('project_id', options.projectId)
        .order('created_at', { ascending: false })
        .order('id')
        .range(from, to);
        
      if (options.search) q = q.ilike('name', `%${options.search}%`);
      if (options.enabled !== undefined) q = q.eq('enable', options.enabled);
      
      return q;
    });

    return data.map((item: any) => this.mapToEntity(item));
  }

  async getSchemeById(id: string): Promise<PromotionScheme | null> {
    const { data, error } = await this.supabase
      .from('promotion_schemes')
      .select(SCHEME_FIELDS)
      .eq('id', id)
      .single();

    if (error) throw error;
    return data ? this.mapToEntity(data) : null;
  }

  async createScheme(input: Omit<PromotionScheme, 'id' | 'created_at' | 'updated_at'>): Promise<PromotionScheme> {
    const payload = this.mapToDB(input);

    const { data, error } = await this.supabase
      .from('promotion_schemes')
      .insert([payload])
      .select(SCHEME_FIELDS)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async updateScheme(id: string, input: Partial<PromotionScheme>): Promise<PromotionScheme> {
    const payload = this.mapToDB(input);

    const { data, error } = await this.supabase
      .from('promotion_schemes')
      .update(payload)
      .eq('id', id)
      .select(SCHEME_FIELDS)
      .single();

    if (error) throw error;
    return this.mapToEntity(data);
  }

  async deleteScheme(id: string): Promise<void> {
    const { error } = await this.supabase
      .from('promotion_schemes')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
}
